import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Paginator from '../components/Paginator';

const PAGE_SIZE = 20;

const STATUS_COLORS = {
  open:      { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.3)',   text: '#22C55E' },
  assigned:  { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)',  text: '#3B82F6' },
  filled:    { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)',  text: '#3B82F6' },
  completed: { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.3)',  text: '#A855F7' },
  expired:   { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   text: '#EF4444' },
  closed:    { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   text: '#EF4444' },
  inactive:  { bg: 'rgba(85,85,85,0.1)',    border: 'rgba(85,85,85,0.3)',    text: 'var(--text-muted)' },
};

const STATUS_LABELS = {
  open: '🟢 Open', assigned: '🔵 Assigned', filled: '🔵 Filled',
  completed: '✅ Completed', expired: '🔴 Expired', closed: '🔴 Closed', inactive: '⬜ Inactive',
};

// Parses MM/DD/YYYY or YYYY-MM-DD strings in local time
const parseDate = (str) => {
  if (!str) return null;
  const parts = str.includes('/') ? str.split('/') : str.split('-');
  if (parts.length !== 3) return null;
  const [a, b, c] = parts.map(Number);
  return str.includes('/')
    ? new Date(c, a - 1, b)   // MM/DD/YYYY
    : new Date(a, b - 1, c);  // YYYY-MM-DD
};

const daysOverdue = (dateStr) => {
  const end = parseDate(dateStr);
  if (!end) return 0;
  return Math.floor((Date.now() - end.getTime()) / 86_400_000);
};

const isOverdue = (job) =>
  ['assigned', 'filled'].includes(job.status) &&
  (() => { const d = parseDate(job.bookingEndDate); return d && d < new Date(); })();

const formatDate = (str) => {
  const d = parseDate(str);
  if (!d) return '—';
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatTs = (ts) => {
  if (!ts?.seconds) return '—';
  return new Date(ts.seconds * 1000).toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const Badge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.inactive;
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const Section = ({ label, children }) => (
  <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '12px 14px' }}>
    <div style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{label}</div>
    {children}
  </div>
);

const Row = ({ label, val, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 12 }}>
    <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>{label}</span>
    <span style={{ color: 'var(--text)', fontSize: 12, textAlign: 'right', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{val ?? '—'}</span>
  </div>
);

export default function JobManagement() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overdue');
  const [selectedJob, setSelectedJob] = useState(null);
  const [confirming, setConfirming] = useState(null); // { id, action }
  const [actionLoading, setActionLoading] = useState(false);
  const [filterHirer, setFilterHirer] = useState('');
  const [filterTitle, setFilterTitle] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(1);

  const uniqueHirers = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const job of jobs) {
      const key = job.hirerId || job.hirerName;
      const label = job.hirerCompanyName || job.hirerName || (job.hirerId ? job.hirerId.slice(0, 8) + '…' : null);
      if (key && label && !seen.has(key)) {
        seen.add(key);
        result.push({ key, label });
      }
    }
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [jobs]);

  const hasFilters = filterHirer || filterTitle || filterDateFrom || filterDateTo;

  const applyFilters = (list) => list.filter(job => {
    if (filterHirer && (job.hirerId || job.hirerName) !== filterHirer) return false;
    if (filterTitle && !(job.title || '').toLowerCase().includes(filterTitle.toLowerCase())) return false;
    if (filterDateFrom || filterDateTo) {
      const start = parseDate(job.bookingStartDate);
      if (filterDateFrom && (!start || start < parseDate(filterDateFrom))) return false;
      if (filterDateTo   && (!start || start > parseDate(filterDateTo)))   return false;
    }
    return true;
  });

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [activeTab, filterHirer, filterTitle, filterDateFrom, filterDateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'jobs'));
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const overdueJobs   = jobs.filter(isOverdue).sort((a, b) => daysOverdue(b.bookingEndDate) - daysOverdue(a.bookingEndDate));
  const assignedJobs  = jobs.filter(j => ['assigned', 'filled'].includes(j.status) && !isOverdue(j)).sort((a, b) => {
    const aStart = parseDate(a.bookingStartDate)?.getTime() || 0;
    const bStart = parseDate(b.bookingStartDate)?.getTime() || 0;
    return aStart - bStart;
  });
  const openJobs      = jobs.filter(j => j.status === 'open').sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  const completedJobs = jobs.filter(j => j.status === 'completed').sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  const expiredJobs   = jobs.filter(j => ['expired', 'closed'].includes(j.status)).sort((a, b) => (b.expiredAt?.seconds || b.createdAt?.seconds || 0) - (a.expiredAt?.seconds || a.createdAt?.seconds || 0));
  const allJobs       = [...jobs].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  const assignedNoInsurance = assignedJobs.filter(j => !j.preHireConfirmation).length;

  const TABS = [
    { id: 'overdue',   label: '🔴 Overdue',        jobs: overdueJobs,   empty: 'No overdue jobs — platform is healthy!' },
    { id: 'assigned',  label: '🔵 Assigned',        jobs: assignedJobs,  empty: 'No active assigned jobs.' },
    { id: 'open',      label: '🟢 Open',            jobs: openJobs,      empty: 'No open jobs currently.' },
    { id: 'completed', label: '✅ Completed',        jobs: completedJobs, empty: 'No completed jobs yet.' },
    { id: 'expired',   label: '⚫ Expired / Closed', jobs: expiredJobs,   empty: 'No expired or closed jobs.' },
    { id: 'all',       label: '📋 All',              jobs: allJobs,       empty: 'No jobs found.' },
  ];

  const currentTab = TABS.find(t => t.id === activeTab);
  const visibleJobs = applyFilters(currentTab.jobs);

  const updateJob = (id, patch) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));
    setSelectedJob(prev => prev?.id === id ? { ...prev, ...patch } : prev);
  };

  const handleMarkExpired = async (jobId) => {
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'jobs', jobId), {
        status: 'expired',
        expiredAt: serverTimestamp(),
        expiredReason: 'admin_manual',
      });
      updateJob(jobId, { status: 'expired', expiredReason: 'admin_manual' });
    } catch (err) { console.error(err); alert('Failed to update job.'); }
    finally { setActionLoading(false); setConfirming(null); }
  };

  const handleMarkComplete = async (jobId) => {
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'jobs', jobId), {
        status: 'completed',
        completedBy: 'admin',
      });
      updateJob(jobId, { status: 'completed', completedBy: 'admin' });
    } catch (err) { console.error(err); alert('Failed to update job.'); }
    finally { setActionLoading(false); setConfirming(null); }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading jobs…</div>;

  return (
    <div className="animate-fade">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>Job Management</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>Review overdue jobs, manage status, and audit job history</p>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        {overdueJobs.length > 0 && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#EF4444', fontWeight: 700 }}>{overdueJobs.length} overdue</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>need attention</span>
          </div>
        )}
        {assignedNoInsurance > 0 && (
          <div
            onClick={() => setActiveTab('assigned')}
            style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ color: '#F97316', fontWeight: 700 }}>{assignedNoInsurance} no insurance</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>email hirer</span>
          </div>
        )}
        {[
          { count: openJobs.length,      color: '#22C55E', label: 'open' },
          { count: completedJobs.length, color: '#A855F7', label: 'completed' },
          { count: expiredJobs.length,   color: '#EF4444', label: 'expired / closed' },
          { count: jobs.length,          color: 'var(--text)', label: 'total' },
        ].map(({ count, color, label }) => (
          <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 10, padding: '8px 14px' }}>
            <span style={{ color, fontWeight: 700 }}>{count}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> {label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        {/* Left: list */}
        <div style={{ flex: selectedJob ? '0 0 55%' : 1, minWidth: 0 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 14, background: 'var(--surface)', borderRadius: 10, padding: 4, border: '1px solid var(--surface2)' }}>
            {TABS.map(({ id, label, jobs: tj }) => (
              <button key={id} onClick={() => setActiveTab(id)} style={{
                flex: 1, background: activeTab === id ? (id === 'overdue' ? '#EF4444' : '#F97316') : 'transparent',
                border: 'none', color: activeTab === id ? '#fff' : 'var(--text-muted)',
                borderRadius: 8, padding: '7px 4px', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>
                {label} ({tj.length})
              </button>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search title…"
              value={filterTitle}
              onChange={e => setFilterTitle(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 8, color: 'var(--text)', padding: '7px 10px', fontSize: 12, outline: 'none', minWidth: 130, flex: '1 1 130px' }}
            />
            <select
              value={filterHirer}
              onChange={e => setFilterHirer(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 8, color: filterHirer ? 'var(--text)' : 'var(--text-muted)', padding: '7px 10px', fontSize: 12, outline: 'none', minWidth: 150, flex: '1 1 150px' }}
            >
              <option value="">All hirers</option>
              {uniqueHirers.map(h => <option key={h.key} value={h.key}>{h.label}</option>)}
            </select>
            <input
              type="date"
              value={filterDateFrom}
              onChange={e => setFilterDateFrom(e.target.value)}
              title="Booking start on or after"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 8, color: filterDateFrom ? 'var(--text)' : 'var(--text-muted)', padding: '7px 10px', fontSize: 12, outline: 'none', colorScheme: 'dark' }}
            />
            <input
              type="date"
              value={filterDateTo}
              onChange={e => setFilterDateTo(e.target.value)}
              title="Booking start on or before"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 8, color: filterDateTo ? 'var(--text)' : 'var(--text-muted)', padding: '7px 10px', fontSize: 12, outline: 'none', colorScheme: 'dark' }}
            />
            {hasFilters && (
              <button
                onClick={() => { setFilterHirer(''); setFilterTitle(''); setFilterDateFrom(''); setFilterDateTo(''); }}
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#EF4444', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Clear
              </button>
            )}
          </div>

          {/* List */}
          {currentTab.jobs.length === 0 ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              {currentTab.empty}
            </div>
          ) : visibleJobs.length === 0 ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              No jobs match the current filters.
            </div>
          ) : (() => {
            const totalPages = Math.ceil(visibleJobs.length / PAGE_SIZE);
            const paginatedJobs = visibleJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
            return (
            <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {paginatedJobs.map(job => {
                const overdue = isOverdue(job);
                const days = overdue ? daysOverdue(job.bookingEndDate) : 0;
                const isSelected = selectedJob?.id === job.id;
                const isConfirming = confirming?.id === job.id;
                const canAct = ['assigned', 'filled'].includes(job.status);

                return (
                  <div key={job.id}
                    onClick={() => setSelectedJob(isSelected ? null : job)}
                    style={{
                      background: isSelected ? 'var(--surface)' : 'var(--surface)',
                      border: `1px solid ${isSelected ? 'rgba(249,115,22,0.4)' : overdue ? 'rgba(239,68,68,0.25)' : 'var(--surface2)'}`,
                      borderRadius: 12, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <Badge status={job.status} />

                      <div style={{ flex: 1, minWidth: 100 }}>
                        <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>{job.title || 'Untitled Job'}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                          {job.hirerCompanyName || job.hirerName || (job.hirerId ? job.hirerId.slice(0, 8) + '…' : '—')}
                        </div>
                      </div>

                      {overdue && (
                        <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                          {days}d overdue
                        </span>
                      )}

                      {['assigned', 'filled'].includes(job.status) && (
                        <span style={{
                          background: job.preHireConfirmation ? 'rgba(34,197,94,0.1)' : 'rgba(249,115,22,0.1)',
                          border: `1px solid ${job.preHireConfirmation ? 'rgba(34,197,94,0.3)' : 'rgba(249,115,22,0.35)'}`,
                          color: job.preHireConfirmation ? '#22C55E' : '#F97316',
                          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap',
                        }}>
                          {job.preHireConfirmation ? '✓ Insured' : '⚠ No Insurance'}
                        </span>
                      )}

                      <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'right', flexShrink: 0 }}>
                        <div>Start: {formatDate(job.bookingStartDate)}</div>
                        <div>End: {formatDate(job.bookingEndDate)}</div>
                        {job.assignedDriverName && <div style={{ color: '#3B82F6', marginTop: 2 }}>👤 {job.assignedDriverName}</div>}
                      </div>

                      {/* Inline actions — only for assigned/filled */}
                      {canAct && (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          {isConfirming ? (
                            <>
                              <span style={{ color: 'var(--text-secondary)', fontSize: 12, alignSelf: 'center' }}>
                                {confirming.action === 'expire' ? 'Mark expired?' : 'Mark complete?'}
                              </span>
                              <button disabled={actionLoading}
                                onClick={() => confirming.action === 'expire' ? handleMarkExpired(job.id) : handleMarkComplete(job.id)}
                                style={{ background: confirming.action === 'expire' ? '#EF4444' : '#22C55E', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                {actionLoading ? '…' : 'Confirm'}
                              </button>
                              <button onClick={() => setConfirming(null)}
                                style={{ background: 'var(--border)', border: 'none', color: 'var(--text-secondary)', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setConfirming({ id: job.id, action: 'expire' })}
                                style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#EF4444', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Expire
                              </button>
                              <button onClick={() => setConfirming({ id: job.id, action: 'complete' })}
                                style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)', color: '#A855F7', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Complete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Nudge indicators */}
                    {(job.overdueNudgeSentAt || job.overdueNudge3Sent) && (
                      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                        ⚠️ Nudge sent {formatTs(job.overdueNudgeSentAt)}
                        {job.overdueNudge3Sent && ' · 2nd nudge sent'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {visibleJobs.length > PAGE_SIZE && (
              <Paginator page={page} totalPages={totalPages} totalItems={visibleJobs.length} pageSize={PAGE_SIZE} onChange={setPage} />
            )}
            </>
            );
          })()}
        </div>

        {/* Right: detail panel */}
        {selectedJob && (
          <div style={{
            flex: 1, background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 16,
            padding: 24, alignSelf: 'flex-start', position: 'sticky', top: 0,
            maxHeight: '85vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 700, color: 'var(--text)', flex: 1, marginRight: 12 }}>
                {selectedJob.title || 'Job Details'}
              </h2>
              <button onClick={() => setSelectedJob(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>
                ×
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              <Badge status={selectedJob.status} />
              {isOverdue(selectedJob) && (
                <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700 }}>
                  {daysOverdue(selectedJob.bookingEndDate)}d overdue
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Section label="Job Info">
                <Row label="Title"     val={selectedJob.title} />
                <Row label="Type"      val={selectedJob.jobType} />
                <Row label="Truck"     val={selectedJob.truckType} />
                <Row label="License"   val={selectedJob.licenseRequired} />
                <Row label="Pay Rate"  val={selectedJob.payRate} />
                <Row label="Duration"  val={selectedJob.duration} />
                <Row label="Location"  val={selectedJob.location} />
              </Section>

              <Section label="Hirer">
                <Row label="Company"  val={selectedJob.hirerCompanyName} />
                <Row label="Name"     val={selectedJob.hirerName} />
                <Row label="ID"       val={selectedJob.hirerId} mono />
                <Row label="Verified" val={selectedJob.hirerVerificationStatus} />
              </Section>

              <Section label="Assignment">
                <Row label="Driver"     val={selectedJob.assignedDriverName} />
                <Row label="Driver ID"  val={selectedJob.assignedDriverId} mono />
                <Row label="Start Date" val={formatDate(selectedJob.bookingStartDate)} />
                <Row label="End Date"   val={formatDate(selectedJob.bookingEndDate)} />
              </Section>

              <Section label="Lifecycle">
                <Row label="Created"       val={formatTs(selectedJob.createdAt)} />
                <Row label="Expired At"    val={formatTs(selectedJob.expiredAt)} />
                <Row label="Expire Reason" val={selectedJob.expiredReason} />
                <Row label="Completed By"  val={selectedJob.completedBy} />
                <Row label="Nudge Sent"    val={formatTs(selectedJob.overdueNudgeSentAt)} />
                <Row label="2nd Nudge"     val={selectedJob.overdueNudge3Sent ? 'Yes' : '—'} />
              </Section>

              {selectedJob.description && (
                <Section label="Description">
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>{selectedJob.description}</p>
                </Section>
              )}

              <Section label="Insurance Confirmation">
                {selectedJob.preHireConfirmation ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ color: '#22C55E', fontSize: 15, marginTop: 1 }}>✓</span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.6 }}>
                      Insurance confirmed on{' '}
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                        {formatTs(selectedJob.preHireConfirmation.confirmedAt)}
                      </span>
                      {selectedJob.preHireConfirmation.driverName && (
                        <>
                          {' '}for{' '}
                          <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                            {selectedJob.preHireConfirmation.driverName}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                ) : (
                  <div style={{
                    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                    borderRadius: 8, padding: '8px 12px',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{ fontSize: 15 }}>⚠️</span>
                    <span style={{ color: '#EF4444', fontSize: 12, fontWeight: 600 }}>
                      No insurance confirmation on record
                    </span>
                  </div>
                )}
              </Section>

              <Section label="Applicants">
                <Row label="Count" val={selectedJob.applicants?.length ?? 0} />
              </Section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
